/**
 * Wishlist page — reads localStorage and renders product cards client-side.
 */

/**
 * @typedef {{ id: string, title: string, price: string, image: string, url: string, handle: string }} WishlistItem
 */

const STORAGE_KEY = 'tickandpick_wishlist';

/** @returns {WishlistItem[]} */
function getWishlistItems() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const raw = JSON.parse(stored || '[]');
    /** @type {WishlistItem[]} */
    const items = [];
    for (const item of raw) {
      if (typeof item === 'string' && item) {
        items.push({ id: item, title: item, price: '', image: '', url: '/', handle: '' });
      } else if (item && typeof item.id === 'string') {
        items.push({
          id: item.id,
          title: String(item.title || ''),
          price: String(item.price || ''),
          image: String(item.image || ''),
          url: String(item.url || '/'),
          handle: String(item.handle || ''),
        });
      }
    }
    return items;
  } catch {
    return [];
  }
}

function escHtml(/** @type {string} */ str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const HEART_OUTLINE = `<svg class="wishlist-button__icon wishlist-button__icon--outline" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
const HEART_FILLED = `<svg class="wishlist-button__icon wishlist-button__icon--filled" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;

function renderCard(/** @type {WishlistItem} */ item, /** @type {string} */ msgAdded, /** @type {string} */ msgRemoved) {
  const title = item.title || item.id || '';
  const url = item.url || '/';

  const media = item.image
    ? `<img src="${escHtml(item.image)}" alt="${escHtml(title)}" class="wishlist-card__image" loading="lazy">`
    : `<div class="wishlist-card__image-placeholder" aria-hidden="true">${escHtml(title.substring(0, 60))}</div>`;

  return `
    <div class="wishlist-card">
      <a class="wishlist-card__link" href="${escHtml(url)}">
        <span class="visually-hidden">${escHtml(title)}</span>
      </a>
      <div class="wishlist-card__media">
        ${media}
      </div>
      <div class="wishlist-card__info">
        <p class="wishlist-card__title">${escHtml(title)}</p>
        <p class="wishlist-card__price">${escHtml(item.price || '')}</p>
      </div>
      <div class="wishlist-card__action">
        <wishlist-button
          class="wishlist-button"
          data-product-id="${escHtml(item.id)}"
          data-active="true"
          data-msg-added="${escHtml(msgAdded)}"
          data-msg-removed="${escHtml(msgRemoved)}"
          data-product-handle="${escHtml(item.handle || '')}"
          data-product-title="${escHtml(title)}"
          data-product-price="${escHtml(item.price || '')}"
          data-product-image="${escHtml(item.image || '')}"
          data-product-url="${escHtml(url)}"
        >
          <button type="button" class="wishlist-button__btn button-unstyled" aria-pressed="true" aria-label="${escHtml(msgRemoved)}">
            ${HEART_OUTLINE}
            ${HEART_FILLED}
          </button>
        </wishlist-button>
      </div>
    </div>`;
}

function updateCount(/** @type {number} */ count) {
  const countEl = document.getElementById('wishlist-count');
  if (!countEl) return;
  countEl.textContent = count > 0 ? String(count) : '';
}

function showEmptyState(/** @type {HTMLElement | null} */ emptyEl, /** @type {HTMLElement | null} */ gridEl) {
  emptyEl?.removeAttribute('hidden');
  gridEl?.setAttribute('hidden', '');
  updateCount(0);
}

async function initWishlistPage() {
  const pageEl = document.getElementById('wishlist-page');
  const grid = /** @type {HTMLElement | null} */ (document.getElementById('wishlist-grid'));
  const emptyState = document.getElementById('wishlist-empty');
  const loadingState = document.getElementById('wishlist-loading');

  if (!grid || !pageEl) return;

  const msgAdded = pageEl.dataset.msgAdded || 'Added to wishlist';
  const msgRemoved = pageEl.dataset.msgRemoved || 'Removed from wishlist';

  const items = getWishlistItems();

  loadingState?.remove();

  if (items.length === 0) {
    showEmptyState(emptyState, grid);
    return;
  }

  await customElements.whenDefined('wishlist-button');

  grid.removeAttribute('hidden');
  updateCount(items.length);

  items.forEach((item, i) => {
    const li = document.createElement('li');
    li.className = 'wishlist-page__grid-item';
    li.style.setProperty('--stagger-i', String(i));
    li.dataset.productId = String(item.id);
    li.innerHTML = renderCard(item, msgAdded, msgRemoved);
    grid.appendChild(li);
  });

  window.addEventListener('wishlist:updated', (/** @type {Event} */ event) => {
    const { productId, isActive } = /** @type {CustomEvent} */ (event).detail;
    if (isActive) return;

    const li = grid.querySelector(`[data-product-id="${productId}"]`);
    if (!li) return;

    li.classList.add('wishlist-page__grid-item--removing');
    li.addEventListener('animationend', () => {
      li.remove();
      const remaining = grid.querySelectorAll('.wishlist-page__grid-item').length;
      updateCount(remaining);
      if (remaining === 0) showEmptyState(emptyState, grid);
    }, { once: true });
  });
}

initWishlistPage();
